import React from 'react';
import { BlurFade } from '@/components/effects';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { User } from 'lucide-react';

// --- Reusable mini SVG shapes (matching existing patterns) ---

const StarBurst8: React.FC<{ size?: number; color?: string; rotate?: number }> = ({
  size = 16,
  color = '#7CFC00',
  rotate = 0,
}) => {
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size / 2;
  const innerR = size / 3.5;
  const totalPoints = 16;
  const pts = Array.from({ length: totalPoints }, (_, i) => {
    const angle = (Math.PI / 8) * i - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
  }).join(' ');
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill={color}
      style={{ transform: `rotate(${rotate}deg)`, display: 'inline-block' }}
      aria-hidden="true"
    >
      <polygon points={pts} />
    </svg>
  );
};

// Halftone dot pattern (shared comic style)
const HalftoneDots: React.FC<{ opacity?: number; color?: string }> = ({
  opacity = 0.03,
  color = 'rgba(124,252,0,1)',
}) => (
  <div
    aria-hidden="true"
    className="absolute inset-0 pointer-events-none"
    style={{
      backgroundImage: `radial-gradient(circle, ${color} 1px, transparent 1px)`,
      backgroundSize: '20px 20px',
      opacity,
      zIndex: 0,
    }}
  />
);

// Silhouette placeholder component
const SilhouettePlaceholder: React.FC<{ accentColor: string }> = ({ accentColor }) => (
  <div
    className="flex items-center justify-center w-full h-full"
    style={{
      background: `radial-gradient(ellipse 40% 60% at 50% 45%, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 100%)`,
    }}
  >
    <div
      className="relative flex items-center justify-center rounded-full"
      style={{
        width: '140px',
        height: '140px',
        background: `radial-gradient(circle at 35% 35%, rgba(0,0,0,0.8), rgba(0,0,0,0.95))`,
        border: `2px solid ${accentColor}`,
        boxShadow: `0 0 40px ${accentColor.replace('1)', '0.15)')}`,
      }}
    >
      <User
        size={80}
        color={accentColor}
        strokeWidth={0.8}
        style={{ opacity: 0.6 }}
      />
    </div>
  </div>
);

// Comic-book style "POW" label (shared comic style)
const ComicLabel: React.FC<{
  text: string;
  color?: string;
  bgColor?: string;
  rotate?: number;
  style?: React.CSSProperties;
}> = ({ text, color = '#0a0a0f', bgColor = '#7CFC00', rotate = -8, style }) => (
  <div
    aria-hidden="true"
    style={{
      display: 'inline-block',
      background: bgColor,
      color,
      fontFamily: "'Bangers', cursive",
      fontSize: '0.9rem',
      letterSpacing: '0.12em',
      padding: '2px 10px',
      border: '3px solid rgba(0,0,0,0.2)',
      boxShadow: '3px 3px 0 rgba(0,0,0,0.25)',
      transform: `rotate(${rotate}deg)`,
      userSelect: 'none',
      lineHeight: 1.3,
      ...style,
    }}
  >
    {text}
  </div>
);

// Cartoon section divider (matching CharacterGallery)
const CartoonDivider: React.FC<{ color?: string }> = ({ color = 'rgba(124,252,0,0.35)' }) => (
  <div aria-hidden="true" className="flex items-center justify-center gap-2" style={{ color }}>
    <div className="h-px flex-1 max-w-[120px]" style={{ background: `linear-gradient(90deg, transparent, ${color})` }} />
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
      <polygon points="7,0 8.5,5.5 14,5.5 9.5,8.5 11,14 7,11 3,14 4.5,8.5 0,5.5 5.5,5.5" />
    </svg>
    <svg width="12" height="18" viewBox="0 0 12 18" fill="currentColor">
      <path d="M7 0L0 10h5L4 18l8-10H7z" />
    </svg>
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
      <polygon points="7,0 8.5,5.5 14,5.5 9.5,8.5 11,14 7,11 3,14 4.5,8.5 0,5.5 5.5,5.5" />
    </svg>
    <div className="h-px flex-1 max-w-[120px]" style={{ background: `linear-gradient(90deg, ${color}, transparent)` }} />
  </div>
);

interface TeamMember {
  name: string;
  role: string;
  image: string;
  tiltDeg: number;
  accentColor: string;
  glowColor: string;
  labelText: string;
  labelBg: string;
  labelTextColor: string;
  labelRotate: number;
}

const TEAM: TeamMember[] = [
  {
    name: 'V',
    role: 'Founder / Main Artist',
    image:
      'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69c61af0dcc9e20937028730',
    tiltDeg: -1.5,
    accentColor: '#7CFC00',
    glowColor: 'rgba(124,252,0,0.22)',
    labelText: 'THE BIG BOSS',
    labelBg: '#7CFC00',
    labelTextColor: '#0a0a0f',
    labelRotate: -7,
  },
  {
    name: 'JD',
    role: 'Co-Founder / Project Lead / Dev',
    image:
      'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69c61b0db125032ef631fc49',
    tiltDeg: 1.5,
    accentColor: '#E63946',
    glowColor: 'rgba(230,57,70,0.22)',
    labelText: 'THE MANAGER',
    labelBg: '#E63946',
    labelTextColor: '#fff',
    labelRotate: 6,
  },
  {
    name: '0xM',
    role: 'Dev / IP Specialist',
    image: 'SILHOUETTE',
    tiltDeg: -2.2,
    accentColor: '#00D9FF',
    glowColor: 'rgba(0,217,255,0.22)',
    labelText: 'THE DEV',
    labelBg: '#00D9FF',
    labelTextColor: '#0a0a0f',
    labelRotate: -6,
  },
  {
    name: 'NN',
    role: 'Animator',
    image: 'SILHOUETTE',
    tiltDeg: 0.8,
    accentColor: '#FF006E',
    glowColor: 'rgba(255,0,110,0.22)',
    labelText: 'THE ANIMATOR',
    labelBg: '#FF006E',
    labelTextColor: '#fff',
    labelRotate: 5,
  },
  {
    name: 'MELL',
    role: 'Public Relations (PR) / Collab Manager',
    image: 'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69d5922521f73302c16fe648',
    tiltDeg: 1.8,
    accentColor: '#FFD60A',
    glowColor: 'rgba(255,214,10,0.22)',
    labelText: 'THE PROMOTER',
    labelBg: '#FFD60A',
    labelTextColor: '#0a0a0f',
    labelRotate: 7,
  },
];

interface TeamCardProps {
  member: TeamMember;
  animClass: string;
  staggerDelay?: number;
}

const TeamCard: React.FC<TeamCardProps> = ({ member, animClass, staggerDelay = 0 }) => {
  const cardRef = useScrollAnimation<HTMLDivElement>(0.05);

  return (
    <div
      ref={cardRef}
      className={animClass}
      style={{ transitionDelay: `${staggerDelay}ms` }}
    >
    <div
      className="group relative flex flex-col items-center"
      style={{
        transform: `rotate(${member.tiltDeg}deg)`,
        transition: 'transform 0.35s cubic-bezier(0.34,1.56,0.64,1)',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = `rotate(${member.tiltDeg * 0.3}deg) scale(1.04) translateY(-6px)`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = `rotate(${member.tiltDeg}deg) scale(1) translateY(0)`;
      }}
    >
      {/* Floating comic label above the card */}
      <div
        className="absolute z-20 pointer-events-none"
        style={{ top: '-14px', right: member.tiltDeg < 0 ? '10%' : undefined, left: member.tiltDeg > 0 ? '10%' : undefined }}
      >
        <ComicLabel
          text={member.labelText}
          bgColor={member.labelBg}
          color={member.labelTextColor}
          rotate={member.labelRotate}
        />
      </div>

      {/* Card body */}
      <div
        className="relative rounded-2xl overflow-hidden w-full"
        style={{
          background: 'hsl(var(--card))',
          border: `3px solid ${member.accentColor.replace(')', ', 0.2)')}`,
          boxShadow: `4px 4px 0 rgba(0,0,0,0.55), 0 16px 48px rgba(0,0,0,0.5)`,
          transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
        }}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLDivElement;
          el.style.borderColor = member.accentColor;
          el.style.boxShadow = `4px 4px 0 rgba(0,0,0,0.55), 0 24px 60px rgba(0,0,0,0.6), 0 0 40px ${member.glowColor}`;
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLDivElement;
          el.style.borderColor = member.accentColor.replace(')', ', 0.2)');
          el.style.boxShadow = `4px 4px 0 rgba(0,0,0,0.55), 0 16px 48px rgba(0,0,0,0.5)`;
        }}
      >
        {/* Character image area */}
        <div
          className="relative flex items-center justify-center overflow-hidden"
          style={{
            height: member.image === 'SILHOUETTE' ? '220px' : '280px',
            background: 'radial-gradient(ellipse 70% 80% at 50% 100%, rgba(0,0,0,0.0) 0%, transparent 70%)',
          }}
        >
          {/* Radial glow behind the character */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(ellipse 60% 60% at 50% 70%, ${member.glowColor} 0%, transparent 70%)`,
              zIndex: 0,
            }}
          />

          {/* Subtle halftone dots in image area */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `radial-gradient(circle, ${member.accentColor === '#7CFC00' ? 'rgba(124,252,0,1)' : member.accentColor === '#E63946' ? 'rgba(230,57,70,1)' : `rgba(${parseInt(member.accentColor.slice(1,3), 16)},${parseInt(member.accentColor.slice(3,5), 16)},${parseInt(member.accentColor.slice(5,7), 16)},1)`} 1px, transparent 1px)`,
              backgroundSize: '18px 18px',
              opacity: 0.04,
              zIndex: 0,
            }}
          />

          {/* Character image or silhouette */}
          {member.image === 'SILHOUETTE' ? (
            <SilhouettePlaceholder accentColor={member.accentColor} />
          ) : (
            <img
              src={member.image}
              alt={`${member.name} — ${member.role}`}
              className="relative z-10 h-full w-auto object-contain object-bottom transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              style={{ maxWidth: '100%', display: 'block' }}
            />
          )}
        </div>

        {/* Name & role strip */}
        <div
          className="px-6 py-5"
          style={{
            borderTop: `2px solid ${member.accentColor.replace(')', ', 0.12)')}`,
            background: 'rgba(0,0,0,0.1)',
          }}
        >
          <div className="flex items-center gap-2 mb-1">
            {/* Tiny star accent */}
            <StarBurst8 size={14} color={member.accentColor} rotate={15} />
            <h3
              className="text-3xl uppercase"
              style={{
                fontFamily: "'Bangers', cursive",
                letterSpacing: '0.1em',
                color: member.accentColor,
                lineHeight: 1,
              }}
            >
              {member.name}
            </h3>
            <StarBurst8 size={14} color={member.accentColor} rotate={-10} />
          </div>
          <p
            className="text-sm text-white uppercase tracking-wider"
            style={{ letterSpacing: '0.08em' }}
          >
            {member.role}
          </p>
        </div>
      </div>

      {/* Drop shadow comic offset bar (decorative) */}
      <div
        className="absolute -bottom-2 left-2 right-2 rounded-2xl pointer-events-none"
        style={{
          height: '100%',
          background: 'transparent',
          border: `2px solid ${member.accentColor.replace(')', ', 0.06)')}`,
          zIndex: -1,
          transform: `rotate(${member.tiltDeg * -0.5}deg)`,
        }}
      />
    </div>
    </div>
  );
};

export const TeamSection: React.FC = () => {
  const headingRef = useScrollAnimation<HTMLDivElement>(0.05);

  return (
    <section className="relative py-28 overflow-hidden">
      {/* Background gradient — warm, different from gallery */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, transparent 0%, rgba(124,252,0,0.03) 40%, rgba(230,57,70,0.03) 70%, transparent 100%)',
        }}
      />

      {/* Halftone dots background */}
      <HalftoneDots opacity={0.03} color="rgba(124,252,0,1)" />

      {/* Top + bottom cartoon dividers */}
      <div className="absolute top-0 left-0 right-0 py-2 flex items-center justify-center">
        <CartoonDivider color="rgba(230,57,70,0.3)" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 py-2 flex items-center justify-center">
        <CartoonDivider color="rgba(124,252,0,0.3)" />
      </div>

      {/* Decorative starbursts in corners */}
      <div
        className="absolute pointer-events-none hidden md:block"
        style={{ top: '10%', left: '3%', zIndex: 1 }}
        aria-hidden="true"
      >
        <StarBurst8 size={72} color="rgba(230,57,70,0.14)" rotate={20} />
      </div>
      <div
        className="absolute pointer-events-none hidden md:block"
        style={{ top: '12%', right: '4%', zIndex: 1 }}
        aria-hidden="true"
      >
        <StarBurst8 size={56} color="rgba(124,252,0,0.16)" rotate={-15} />
      </div>
      <div
        className="absolute pointer-events-none hidden md:block"
        style={{ bottom: '10%', left: '5%', zIndex: 1 }}
        aria-hidden="true"
      >
        <StarBurst8 size={44} color="rgba(124,252,0,0.12)" rotate={8} />
      </div>
      <div
        className="absolute pointer-events-none hidden md:block"
        style={{ bottom: '12%', right: '3%', zIndex: 1 }}
        aria-hidden="true"
      >
        <StarBurst8 size={64} color="rgba(230,57,70,0.12)" rotate={-25} />
      </div>

      {/* Decorative comic labels */}
      <div
        className="absolute hidden md:block"
        style={{ top: '7%', left: '1.5%', zIndex: 2 }}
      >
        <ComicLabel text="CREW!" color="#0a0a0f" bgColor="#7CFC00" rotate={-9} />
      </div>
      <div
        className="absolute hidden md:block"
        style={{ bottom: '16%', right: '2%', zIndex: 2 }}
      >
        <ComicLabel text="SQUAD" color="#fff" bgColor="#E63946" rotate={7} />
      </div>

      <div className="container relative z-10">
        {/* Section heading */}
        <div ref={headingRef} className="text-center mb-16 scroll-fade-up">
          <BlurFade delay={0.05}>
            <span
              className="block text-xs font-bold uppercase tracking-[0.3em] mb-3"
              style={{ color: '#E63946' }}
            >
              The People Behind The Chaos
            </span>
          </BlurFade>

          <BlurFade delay={0.1}>
            {/* Heading divider with star */}
            <div className="flex items-center justify-center gap-3 mb-2">
              <div
                className="h-px flex-1 max-w-[80px]"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(124,252,0,0.4))' }}
              />
              <svg width="16" height="16" viewBox="0 0 16 16" fill="#7CFC00" aria-hidden="true">
                <polygon points="8,0 9.5,6 16,6.5 11,10 13,16 8,12.5 3,16 5,10 0,6.5 6.5,6" />
              </svg>
              <div
                className="h-px flex-1 max-w-[80px]"
                style={{ background: 'linear-gradient(90deg, rgba(124,252,0,0.4), transparent)' }}
              />
            </div>

            <h2
              className="text-5xl md:text-7xl font-bold uppercase"
              style={{
                fontFamily: "'Bangers', cursive",
                letterSpacing: '0.08em',
                color: '#7CFC00',
                textShadow: '3px 3px 0 rgba(0,0,0,0.6), 0 0 40px rgba(124,252,0,0.25)',
              }}
            >
              The Team
            </h2>
          </BlurFade>

          <BlurFade delay={0.2}>
            <p className="text-white text-sm md:text-base mt-3 max-w-sm mx-auto">
              The twisted minds bringing Danger Dan! to life — one explosion at a time.
            </p>
          </BlurFade>
        </div>

        {/* Team cards grid */}
        <div className="flex flex-col items-center gap-12 md:gap-20">
          {/* First row: V and JD (2 cards) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-12 md:gap-20 max-w-2xl mx-auto w-full">
            {TEAM.slice(0, 2).map((member, i) => (
              <TeamCard
                key={member.name}
                member={member}
                animClass={i === 0 ? 'scroll-slide-left' : 'scroll-slide-right'}
                staggerDelay={i * 120}
              />
            ))}
          </div>

          {/* Second row: 0xM, NN, FxD (3 cards) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-16 max-w-5xl mx-auto w-full px-4">
            {TEAM.slice(2, 5).map((member, i) => (
              <TeamCard
                key={member.name}
                member={member}
                animClass="scroll-fade-up"
                staggerDelay={(i + 2) * 120}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
